import { z } from 'zod';
import {
  ADDRESS_AREA_MAX,
  ADDRESS_CITY_MAX,
  ADDRESS_LINE_MAX,
  ADDRESS_POSTAL_CODE_MAX,
  ADDRESS_REFUSAL,
  ADDRESS_REGION_MAX,
  POSTAL_KINDS,
  REGION_LABELS,
} from '../../src/domain/geo/address';
import { IdSchema } from './common.schema';

/**
 * Geography for a doctor's address. The request of 6 October 2026 (fifth),
 * section 2.
 *
 * An address is a country and the parts that country's post asks for. The
 * countries, 250 of them, are a file both halves of the system carry word for
 * word, `countries.json`: its entries are `CountryEntrySchema`. The regions
 * and the cities are the server's (GeoNames, CC BY 4.0), asked for a country
 * at a time. An area has no list: it is suggested from the areas the doctors
 * of that city already have.
 */

const part = (max: number) => z.string().trim().max(max, `Keep it to ${String(max)} characters`).nullish();

/**
 * One country of `countries.json`. `lines` is the label, top to bottom, in
 * the tokens `%N` name, `%O` clinic, `%A` the street lines, `%D` area, `%C`
 * city, `%S` region, `%Z` postal code and `%P` PO box; anything else is
 * written as it stands. `upper` names which of C, S and Z the post wants in
 * capitals. `region` is present only where the country has a region field.
 * `postal.kind` is what the country calls its postal code, or `pobox` where it
 * has none: the field is then a PO box, never required and with no pattern.
 * `pattern` is a regular expression the whole value must match, without
 * regard to case. `regionCodes` are the `[short code, name]` pairs the label
 * prints in place of a region's name.
 */
export const CountryEntrySchema = z.object({
  code: z.string().regex(/^[A-Z]{2}$/),
  name: z.string().min(1),
  lines: z.array(z.string().min(1)).min(1),
  upper: z.string().regex(/^[CSZ]*$/),
  region: z
    .object({
      label: z.enum(REGION_LABELS),
      required: z.boolean(),
    })
    .optional(),
  postal: z.object({
    kind: z.enum(POSTAL_KINDS),
    required: z.boolean(),
    pattern: z.string().optional(),
    example: z.string().optional(),
  }),
  regionCodes: z.array(z.tuple([z.string(), z.string()])).optional(),
});

/**
 * A doctor's address as it is served: eleven keys, in this order. `country`
 * is the ISO 3166-1 alpha-2 code in capitals and `countryName` its name in the
 * list. `regionCode` is the region's first-level code inside the country
 * (`03`, `CA`) when the region is one of the list, else null; `cityId` is the
 * city's id in the list, as a string, when the city is one of the list, else
 * null. `lines` is the label as the country's post writes it, the country's
 * name last. While the doctor's `addressNeedsReview` is true the address is
 * only what the conversion of the old text was sure of, and `city` and `line1`
 * may then be null.
 */
export const DoctorAddressSchema = z.object({
  country: z.string(),
  countryName: z.string().nullable(),
  region: z.string().nullable(),
  regionCode: z.string().nullable(),
  city: z.string().nullable(),
  cityId: IdSchema.nullable(),
  area: z.string().nullable(),
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postalCode: z.string().nullable(),
  lines: z.array(z.string()),
});

/**
 * An address as it is sent: the whole of it, which replaces the address the
 * record has. The country is one of the 250 codes, in either case. `region`,
 * `city`, `area`, `line1` and `line2` are kept trimmed with every run of white
 * space inside them as one space; `postalCode` likewise and then in capitals,
 * and without a leading `PO Box` where the country's postal kind is `pobox`.
 * `regionCode` and `cityId` say the region and the city were chosen from the
 * list; the words are sent beside them all the same. `countryName` and
 * `lines`, when a client sends back what it read, are ignored.
 */
export const AddressInputSchema = z.object({
  country: z
    .string({
      required_error: ADDRESS_REFUSAL.chooseCountry,
      invalid_type_error: ADDRESS_REFUSAL.chooseCountry,
    })
    .trim()
    .min(1, ADDRESS_REFUSAL.chooseCountry),
  region: part(ADDRESS_REGION_MAX),
  regionCode: z.string().trim().nullish(),
  city: part(ADDRESS_CITY_MAX),
  cityId: z.string().trim().nullish(),
  area: part(ADDRESS_AREA_MAX),
  line1: part(ADDRESS_LINE_MAX),
  line2: part(ADDRESS_LINE_MAX),
  postalCode: part(ADDRESS_POSTAL_CODE_MAX),
});

/** A region of a country: its first-level code inside the country, and its name. */
export const GeoRegionSchema = z.object({
  code: z.string(),
  name: z.string(),
});

/** `GET /geo/regions`: the regions of one country, by name. */
export const GeoRegionsSchema = z.object({ items: z.array(GeoRegionSchema) });

/**
 * A city as a search suggests one. `id` is the city's id in the list, as a
 * string, or null for a city that is not in the list but that a doctor of the
 * country has, typed by hand.
 */
export const CitySuggestionSchema = z.object({
  id: IdSchema.nullable(),
  name: z.string(),
  regionCode: z.string().nullable(),
  regionName: z.string().nullable(),
});

/** `GET /geo/cities`: the cities that match, the best match first. */
export const GeoCitiesSchema = z.object({ items: z.array(CitySuggestionSchema) });

/** An area a doctor of that city already has. */
export const AreaSuggestionSchema = z.object({ name: z.string() });

/** `GET /geo/areas`: the areas that match, the most used first. */
export const GeoAreasSchema = z.object({ items: z.array(AreaSuggestionSchema) });

/**
 * Where a case is delivered: its doctor's clinic, telephone number and
 * address. The request of 6 October 2026 (fifth), section 2. `address` is the
 * doctor's address only when it has a country, a city and a first line and is
 * not waiting for review, else null; `deliveryAddress` is the old free text,
 * and only when `address` is null. There is no old `mobile` text here: a
 * doctor with only that has `phone` null. Identity, every key of it.
 */
export const DeliverToSchema = z.object({
  clinic: z.string().nullable(),
  phone: z.string().nullable(),
  phoneDisplay: z.string().nullable(),
  address: DoctorAddressSchema.nullable(),
  deliveryAddress: z.string().nullable(),
});

export type CountryEntryPayload = z.infer<typeof CountryEntrySchema>;
export type DoctorAddressPayload = z.infer<typeof DoctorAddressSchema>;
export type AddressInputCommand = z.infer<typeof AddressInputSchema>;
export type GeoRegionPayload = z.infer<typeof GeoRegionSchema>;
export type CitySuggestionPayload = z.infer<typeof CitySuggestionSchema>;
export type AreaSuggestionPayload = z.infer<typeof AreaSuggestionSchema>;
